import {
  LargeHeadingStyled,
  SectionHeaderStyled,
} from "../../../components/custom/texts/texts.styled";
import { Button } from "@nubeteck/components";
import { DatePicker, Layout, Select, Tabs, TabsProps, Skeleton } from "antd";
import React, { useEffect } from "react";
import CareersImport from "./careersImport";
import SubjectsImport from "./subjectsImport";
import StudentsImport from "./studentsImport";
import toast from "react-hot-toast";
import { toastErrorStyle, useSuccessDataImportedToast } from "src/constants";
import {
  useGetCareersImportsMutation,
  useGetCoursesProjectionsImportsMutation,
  useGetProjectionsImportsMutation,
  useGetStudentsImportsMutation,
  useGetSubjectsImportsMutation,
  useImportDataMutation,
} from "src/services/importsService";
import { useGetAdminPeriodsQuery } from "src/services";
import { ArrayUtils } from "@nubeteck/utils";
import ProjectionsImport from "./projectionsImport";
import CoursesProjectionsImport from "./coursesProjectionsImport";
import useDownloadFile from "src/hooks/useDownloadFile";
import { DateSettings } from "../datesSettings";
import { useMediaQuery } from "src/hooks";
import { ColumnContainer } from "src/components/layout/main-layout/main-layout.styled";
import { getImportDataPage } from "../../../Api/content";
import { useLocale } from "../../../i18n/localeContext";

const { Content } = Layout;

const DataImports: React.FC = () => {
  const [getData, { isLoading, isSuccess, error }] = useImportDataMutation();
  const { locale } = useLocale();
  const [importDataPage, setImportDataPage] = React.useState<any>(null);
  const [isLoadingContent, setIsLoadingContent] = React.useState<boolean>(true);
  const [periodoId, setPeriodoId] = React.useState<number>();
  const [anioToImport, setAnioToImport] = React.useState<number | undefined>(0);
  const isMobile = useMediaQuery("(max-width: 1024px)");
  const showSuccessDataImportedToast = useSuccessDataImportedToast();

  const { data: periodos } = useGetAdminPeriodsQuery(anioToImport);
  const { data: periodosParaFiltro } = useGetAdminPeriodsQuery(0);
  const [getCareers, { isLoading: isLoadingCareers, data: dataCareers }] =
    useGetCareersImportsMutation();
  const [getStudents, { isLoading: isLoadingStudents, data: dataStudents }] =
    useGetStudentsImportsMutation();
  const [getSubjects, { isLoading: isLoadingSubjects, data: dataSubjects }] =
    useGetSubjectsImportsMutation();
  const [getProjections, { isLoading: isLoadingProjections, data: dataProjections }] =
    useGetProjectionsImportsMutation();
  const [
    getCoursesProjections,
    { isLoading: isLoadingCoursesProjections, data: dataCoursesProjections },
  ] = useGetCoursesProjectionsImportsMutation();

  const { downloadFile, loadingDownload } = useDownloadFile();

  useEffect(() => {
    let cancel = false;
    setIsLoadingContent(true);
    getImportDataPage(locale)
      .then((res) => {
        const data = res?.data?.attributes || res?.data || res;
        if (!cancel) {
          setImportDataPage(data);
          setIsLoadingContent(false);
        }
      })
      .catch(() => {
        if (!cancel) {
          setImportDataPage(null);
          setIsLoadingContent(false);
        }
      });
    return () => {
      cancel = true;
    };
  }, [locale]);

  React.useEffect(() => {
    if (error) toast.error(error?.data?.message, toastErrorStyle);
    if (isSuccess) {
      showSuccessDataImportedToast();
    }
  }, [error, isSuccess, showSuccessDataImportedToast]);

  const periodosOptions = ArrayUtils.selectLabelValue(periodos?.data ?? [], "nombre", "idPeriodo");
  const filterPeriodosOptions = ArrayUtils.selectLabelValue(
    periodosParaFiltro?.data ?? [],
    "nombre",
    "idPeriodo",
  );

  const items: TabsProps["items"] = [
    {
      key: "1",
      label: importDataPage?.program || "Carreras",
      children: (
        <CareersImport
          isLoading={isLoading || isLoadingCareers}
          getData={getCareers}
          data={dataCareers}
        />
      ),
    },
    {
      key: "2",
      label: importDataPage?.select_Period || "Periodos de selección",
      children: <DateSettings />,
    },
    {
      key: "3",
      label: importDataPage?.Asigns || "Asignaturas",
      children: (
        <SubjectsImport
          isLoading={isLoading || isLoadingSubjects}
          getData={getSubjects}
          data={dataSubjects}
        />
      ),
    },
    {
      key: "4",
      label: importDataPage?.students || "Estudiantes",
      children: (
        <StudentsImport
          isLoading={isLoading || isLoadingStudents}
          getData={getStudents}
          data={dataStudents}
        />
      ),
    },
    {
      key: "5",
      label: importDataPage?.students_proyections || "Proyección de estudiantes",
      children: (
        <ProjectionsImport
          isLoading={isLoading || isLoadingProjections}
          getData={getProjections}
          data={dataProjections}
          periodosOptions={filterPeriodosOptions}
          getExcel={(anio: number, periodoId: number) =>
            downloadFile(
              `${process.env.REACT_APP_BASEURL}/Admins/ProyeccionesExcel/`,
              importDataPage?.students_projection_filename || "Proyecciones de estudiantes",
              "POST",
              {
                anio,
                periodoId,
              },
            )
          }
          isLoadingExcel={loadingDownload}
        />
      ),
    },
    {
      key: "6",
      label: importDataPage?.proyection_courses || "Proyección de cursos",
      children: (
        <CoursesProjectionsImport
          isLoading={isLoading || isLoadingCoursesProjections}
          getData={getCoursesProjections}
          data={dataCoursesProjections}
          periodosOptions={filterPeriodosOptions}
          getExcel={(anio: number, periodoId: number) =>
            downloadFile(
              `${process.env.REACT_APP_BASEURL}/Admins/ProyeccionesAsignaturasExcel/`,
              importDataPage?.courses_projection_filename || "Proyecciones de cursos",
              "POST",
              {
                anio,
                periodoId,
              },
            )
          }
          isLoadingExcel={loadingDownload}
        />
      ),
    },
  ];

  const importData = () => {
    getData(periodoId);
  };

  const filterOption = (input: string, option?: { label: string; value: string }) =>
    (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

  return (
    <ColumnContainer>
      {isLoadingContent ? (
        <>
          <SectionHeaderStyled style={{ height: !isMobile ? "70px" : "auto" }}>
            <Skeleton.Input active size="large" style={{ width: 300, marginBottom: 16 }} />
            <SectionHeaderStyled style={{ gap: "15px", justifyContent: "right" }}>
              <Skeleton.Input active size="default" style={{ width: 250, marginRight: 8 }} />
              <Skeleton.Input active size="default" style={{ width: 100, marginRight: 8 }} />
              <Skeleton.Input active size="default" style={{ width: 200, marginRight: 8 }} />
              <Skeleton.Input active size="default" style={{ width: 150 }} />
            </SectionHeaderStyled>
          </SectionHeaderStyled>
          <Content style={{ marginTop: "16px" }}>
            <Skeleton active paragraph={{ rows: 6 }} />
          </Content>
        </>
      ) : (
        <>
          <SectionHeaderStyled style={{ height: !isMobile ? "70px" : "auto" }}>
            <LargeHeadingStyled>
              {importDataPage?.title || "Importación de datos"}
            </LargeHeadingStyled>
            <SectionHeaderStyled style={{ gap: "15px", justifyContent: "right" }}>
              <label htmlFor="anio">
                {importDataPage?.label_year_period || "Importar datos por año y periodo"}
              </label>
              <DatePicker
                id="anio"
                picker="year"
                onChange={(e) => {
                  setAnioToImport(e?.toDate().getFullYear());
                }}
                placeholder={importDataPage?.year || "Año"}
              />

              <Select
                options={periodosOptions}
                filterOption={filterOption}
                placeholder={importDataPage?.select_option || "Seleccione un periodo"}
                showSearch
                optionFilterProp="children"
                allowClear
                onChange={(e) => {
                  setPeriodoId(e);
                }}
              />

              <Button type="primary" onClick={importData} disabled={!periodoId || isLoading}>
                {importDataPage?.import_button || "Importar datos"}
              </Button>
            </SectionHeaderStyled>
          </SectionHeaderStyled>

          <Content style={{ marginTop: "16px" }}>
            <Tabs
              tabBarStyle={{
                justifyContent: "space-between",
                width: "100%",
              }}
              defaultActiveKey="month"
              items={items}
            />
          </Content>
        </>
      )}
    </ColumnContainer>
  );
};

export default DataImports;
